import { useTranslation } from 'react-i18next'
import { ShieldCheck, BarChart3, Megaphone, ExternalLink } from 'lucide-react'
import { usePageSEO } from '@/hooks/usePageSEO'
import {
  LegalPageLayout,
  LegalSection,
  LegalList,
  LegalCallout,
  type LegalSectionItem,
} from '@/components/legal/LegalPageLayout'
import { cn } from '@/lib/utils'

const SECTION_KEYS = [
  'whatAreCookies',
  'categories',
  'cookieList',
  'manage',
  'updates',
  'contact',
] as const

const COOKIE_ITEMS = ['consent', 'language', 'session'] as const

const BROWSER_LINKS: { key: 'chrome' | 'firefox' | 'safari' | 'edge'; url: string }[] = [
  { key: 'chrome', url: 'https://support.google.com/chrome/answer/95647' },
  {
    key: 'firefox',
    url: 'https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer',
  },
  {
    key: 'safari',
    url: 'https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac',
  },
  {
    key: 'edge',
    url: 'https://support.microsoft.com/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09',
  },
]

export default function CookiePolicyPage() {
  const { t } = useTranslation()

  usePageSEO({
    title: t('meta.cookiePolicy.title'),
    description: t('meta.cookiePolicy.description'),
    ogTitle: t('meta.cookiePolicy.ogTitle'),
    ogDescription: t('meta.cookiePolicy.ogDescription'),
  })

  const toc: LegalSectionItem[] = SECTION_KEYS.map((key) => ({
    id: `cookie-${key}`,
    number: t(`legal.cookiePolicy.sections.${key}.number`),
    title: t(`legal.cookiePolicy.sections.${key}.title`),
  }))

  return (
    <LegalPageLayout
      documentNumber="02 · COOK"
      eyebrow={t('legal.cookiePolicy.eyebrow')}
      title={t('cookiePolicy.title')}
      subtitle={t('legal.cookiePolicy.headerSubtitle')}
      intro={t('legal.cookiePolicy.intro')}
      toc={toc}
    >
      {/* 01 — What are cookies */}
      <LegalSection
        id="cookie-whatAreCookies"
        number={t('legal.cookiePolicy.sections.whatAreCookies.number')}
        title={t('legal.cookiePolicy.sections.whatAreCookies.title')}
        body={t('legal.cookiePolicy.sections.whatAreCookies.body')}
      />

      {/* 02 — Categories */}
      <LegalSection
        id="cookie-categories"
        number={t('legal.cookiePolicy.sections.categories.number')}
        title={t('legal.cookiePolicy.sections.categories.title')}
        body={t('legal.cookiePolicy.sections.categories.body')}
      >
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl">
          <CategoryCard
            index="01"
            icon={<ShieldCheck className="h-5 w-5" strokeWidth={2} />}
            label={t('legal.cookiePolicy.sections.categories.necessaryLabel')}
            description={t(
              'legal.cookiePolicy.sections.categories.necessaryDesc'
            )}
            active
          />
          <CategoryCard
            index="02"
            icon={<BarChart3 className="h-5 w-5" strokeWidth={2} />}
            label={t('legal.cookiePolicy.sections.categories.analyticsLabel')}
            description={t(
              'legal.cookiePolicy.sections.categories.analyticsDesc'
            )}
            active={false}
          />
          <CategoryCard
            index="03"
            icon={<Megaphone className="h-5 w-5" strokeWidth={2} />}
            label={t('legal.cookiePolicy.sections.categories.marketingLabel')}
            description={t(
              'legal.cookiePolicy.sections.categories.marketingDesc'
            )}
            active={false}
          />
        </div>
      </LegalSection>

      {/* 03 — Cookie list */}
      <LegalSection
        id="cookie-cookieList"
        number={t('legal.cookiePolicy.sections.cookieList.number')}
        title={t('legal.cookiePolicy.sections.cookieList.title')}
        body={t('legal.cookiePolicy.sections.cookieList.body')}
      >
        <CookieTable />
        <LegalCallout
          label={t('legal.cookiePolicy.sections.categories.analyticsLabel')}
          tone="warning"
        >
          {t('legal.cookiePolicy.sections.cookieList.analyticsNote')}
        </LegalCallout>
        <LegalCallout
          label={t('legal.cookiePolicy.sections.categories.marketingLabel')}
          tone="warning"
        >
          {t('legal.cookiePolicy.sections.cookieList.marketingNote')}
        </LegalCallout>
      </LegalSection>

      {/* 04 — Manage */}
      <LegalSection
        id="cookie-manage"
        number={t('legal.cookiePolicy.sections.manage.number')}
        title={t('legal.cookiePolicy.sections.manage.title')}
        body={t('legal.cookiePolicy.sections.manage.body')}
      >
        <LegalList
          items={
            t('legal.cookiePolicy.sections.manage.items', {
              returnObjects: true,
            }) as string[]
          }
        />
        <BrowserLinks />
        <LegalCallout>
          {t('legal.cookiePolicy.sections.manage.browserNote')}
        </LegalCallout>
      </LegalSection>

      {/* 05 — Updates */}
      <LegalSection
        id="cookie-updates"
        number={t('legal.cookiePolicy.sections.updates.number')}
        title={t('legal.cookiePolicy.sections.updates.title')}
        body={t('legal.cookiePolicy.sections.updates.body')}
      />

      {/* 06 — Contact */}
      <LegalSection
        id="cookie-contact"
        number={t('legal.cookiePolicy.sections.contact.number')}
        title={t('legal.cookiePolicy.sections.contact.title')}
        body={t('legal.cookiePolicy.sections.contact.body')}
      />
    </LegalPageLayout>
  )
}

function CategoryCard({
  index,
  icon,
  label,
  description,
  active,
}: {
  index: string
  icon: React.ReactNode
  label: string
  description: string
  active: boolean
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'relative bg-white border p-5 transition-colors',
        active ? 'border-brand-500' : 'border-ink-200'
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute top-0 left-0 right-0 h-[2px]',
          active ? 'bg-brand-500' : 'bg-ink-300'
        )}
      />
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center',
            active
              ? 'bg-brand-500 text-white'
              : 'bg-ink-100 text-ink-500'
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
        <span className="font-display font-bold tabular-nums text-[11px] tracking-[0.18em] text-ink-400">
          {index}
        </span>
      </div>
      <h3 className="mt-4 font-display font-bold text-base text-ink-900">
        {label}
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-ink-600 fx-text-pretty">
        {description}
      </p>
      <p
        className={cn(
          'mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em]',
          active ? 'text-brand-600' : 'text-ink-400'
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'block h-1.5 w-1.5 rounded-full',
            active ? 'bg-brand-500' : 'bg-ink-300'
          )}
        />
        {active ? t('cookie.on') : t('cookie.off')}
      </p>
    </div>
  )
}

function CookieTable() {
  const { t } = useTranslation()

  return (
    <div className="mt-6 overflow-x-auto border border-ink-200 bg-white">
      <span
        aria-hidden="true"
        className="block h-[3px] w-full bg-brand-500"
      />
      <table className="w-full text-left text-[13px] border-collapse">
        <thead>
          <tr className="bg-ink-50">
            {(['name', 'provider', 'purpose', 'duration', 'type'] as const).map(
              (col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-3 font-display font-bold text-[10px] uppercase tracking-[0.2em] text-ink-700 border-b border-ink-200"
                >
                  {t(`legal.cookiePolicy.sections.cookieList.headers.${col}`)}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {COOKIE_ITEMS.map((key, i) => {
            const isLast = i === COOKIE_ITEMS.length - 1
            return (
              <tr
                key={key}
                className={cn(
                  !isLast && 'border-b border-ink-200',
                  'hover:bg-ink-50/50 transition-colors'
                )}
              >
                <td className="px-4 py-3.5 font-mono text-[12px] text-ink-900">
                  {t(
                    `legal.cookiePolicy.sections.cookieList.items.${key}.name`
                  )}
                </td>
                <td className="px-4 py-3.5 text-ink-700">
                  {t(
                    `legal.cookiePolicy.sections.cookieList.items.${key}.provider`
                  )}
                </td>
                <td className="px-4 py-3.5 text-ink-700 leading-relaxed min-w-[220px]">
                  {t(
                    `legal.cookiePolicy.sections.cookieList.items.${key}.purpose`
                  )}
                </td>
                <td className="px-4 py-3.5 text-ink-700 whitespace-nowrap">
                  {t(
                    `legal.cookiePolicy.sections.cookieList.items.${key}.duration`
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-brand-500/10 text-brand-700 text-[10px] font-bold uppercase tracking-[0.18em] border border-brand-500/30">
                    <span
                      aria-hidden="true"
                      className="block h-1.5 w-1.5 rounded-full bg-brand-500"
                    />
                    {t(
                      `legal.cookiePolicy.sections.cookieList.items.${key}.type`
                    )}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function BrowserLinks() {
  const { t } = useTranslation()
  return (
    <ul className="mt-6 flex flex-wrap gap-2 max-w-3xl">
      {BROWSER_LINKS.map(({ key, url }) => (
        <li key={key}>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'group inline-flex items-center gap-2 bg-white border border-ink-200 px-3.5 py-2',
              'text-xs font-semibold text-ink-700',
              'hover:border-brand-500 hover:text-brand-600 transition-colors'
            )}
          >
            {t(`legal.cookiePolicy.sections.manage.browserLinks.${key}`)}
            <ExternalLink
              className="h-3 w-3 text-ink-400 group-hover:text-brand-500 transition-colors"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </a>
        </li>
      ))}
    </ul>
  )
}

